import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import React from "react";

import { Error as ErrorComponent } from "@krea/shared/design-system/fields/Error";
import { Label } from "@krea/shared/design-system/fields/Label";
import { cn } from "@krea/shared/utils";

type CheckboxProps = Omit<
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
  "onCheckedChange" | "onChange"
> & {
  label?: React.ReactNode;
  labelClassName?: string;
  error?: string;
  labelUnbolded?: string;
  onChange?: (checked: boolean) => void;
};

const CheckBox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      className,
      disabled,
      tabIndex,
      error,
      checked,
      onKeyDown,
      onChange,
      id,
      labelUnbolded,
      label,
      labelClassName,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={cn("", className)}>
        <Label
          className={cn(
            "flex items-center font-normal text-gray-900",
            { "text-gray-500": disabled },
            labelClassName,
          )}
          htmlFor={id}
        >
          <CheckboxPrimitive.Root
            data-test-id="consent-checkbox"
            ref={ref}
            className={cn(
              "peer mr-2 h-[19px] w-[19px] shrink-0 rounded-sm border-2 border-gray-500 data-[state=checked]:border-primary shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-white",
              { "border-red-500": error },
              { "bg-white": !checked },
            )}
            checked={checked}
            id={id}
            disabled={disabled}
            tabIndex={tabIndex}
            onKeyDown={onKeyDown}
            onCheckedChange={(checked) => onChange?.(checked === true)}
            {...props}
          >
            <CheckboxPrimitive.Indicator
              className={cn(
                "flex items-center justify-center text-current",
              )}
            >
              <Check
                strokeWidth={4}
                className="ml-px h-[16px] w-[16px]"
              />
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>
          {label && <span>{label}</span>}

          {labelUnbolded && (
            <span className="font-normal">{labelUnbolded}</span>
          )}
        </Label>
        {error && (
          <ErrorComponent className="ml-[26px] mt-1">
            {error}
          </ErrorComponent>
        )}
      </div>
    );
  },
);
CheckBox.displayName = CheckboxPrimitive.Root.displayName;

// eslint-disable-next-line import/no-default-export
export default CheckBox;
